import { useEffect, useRef } from 'react';

/**
 * Adds `.is-revealed` to the element when it enters the viewport.
 * Triggers only ONCE. Disconnects on cleanup.
 * Honors prefers-reduced-motion via CSS (no JS branching needed).
 *
 * Usage:
 *   const ref = useReveal();
 *   <div ref={ref} className="reveal">...</div>
 *
 * Options:
 *   threshold:    fraction of element visible to trigger (default 0.15)
 *   rootMargin:   margin around root (default '0px 0px -40px 0px')
 */
export const useReveal = ({ threshold = 0.15, rootMargin = '0px 0px -40px 0px' } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // If IntersectionObserver is unavailable, reveal immediately.
    if (typeof IntersectionObserver === 'undefined') {
      node.classList.add('is-revealed');
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
};
